import React, { useState, useEffect, useCallback } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app, UserRole } from '../../contexts/AuthContext';
import { useLocale } from '../../contexts/LocaleContext';
import './SharedComponents.css';

interface OrgUser {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  isPending?: boolean;
}

interface OrgManageUsersProps {
  organizationId: string;
  organizationName: string;
  isOpen: boolean;
  onClose: () => void;
  availableRoles?: UserRole[];
}

export const OrgManageUsers: React.FC<OrgManageUsersProps> = ({
  organizationId,
  organizationName,
  isOpen,
  onClose,
  availableRoles = ['org_admin', 'org_user', 'viewer'],
}) => {
  const { t } = useLocale();
  const functions = getFunctions(app, 'us-central1');

  const [users, setUsers] = useState<OrgUser[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserName, setNewUserName] = useState('');
  const [newUserRole, setNewUserRole] = useState<UserRole>('org_user');
  const [isAdding, setIsAdding] = useState(false);

  const loadUsers = useCallback(async () => {
    if (!organizationId) return;

    setIsLoading(true);
    try {
      const getOrgUsers = httpsCallable(functions, 'getOrgUsers');
      const result = await getOrgUsers({ organizationId });
      setUsers((result.data as any).users || []);
    } catch (err: any) {
      console.error('Error loading org users:', err);
      setUsers([]);
    }
    setIsLoading(false);
  }, [organizationId, functions]);

  // Load users when modal opens
  useEffect(() => {
    if (isOpen && organizationId) {
      loadUsers();
    }
  }, [isOpen, organizationId, loadUsers]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setShowAddForm(false);
      setNewUserEmail('');
      setNewUserName('');
      setNewUserRole('org_user');
    }
  }, [isOpen]);

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newUserEmail.trim()) return;

    setIsAdding(true);
    try {
      const addUserToOrganization = httpsCallable(functions, 'addUserToOrganization');
      await addUserToOrganization({
        organizationId,
        email: newUserEmail.trim(),
        name: newUserName.trim() || newUserEmail.split('@')[0],
        role: newUserRole,
      });
      setNewUserEmail('');
      setNewUserName('');
      setShowAddForm(false);
      await loadUsers();
    } catch (err: any) {
      console.error('Error adding org user:', err);
      alert(err.message || t('firm.addUserError'));
    }
    setIsAdding(false);
  };

  const handleRemoveUser = async (userId: string) => {
    if (!window.confirm(t('firm.confirmRemoveUser'))) return;

    try {
      const removeUserFromOrganization = httpsCallable(functions, 'removeUserFromOrganization');
      await removeUserFromOrganization({
        organizationId,
        userId,
      });
      await loadUsers();
    } catch (err: any) {
      console.error('Error removing user:', err);
      alert(err.message || t('firm.removeUserError'));
    }
  };

  const handleUpdateRole = async (userId: string, newRole: string) => {
    try {
      const updateOrgUserRole = httpsCallable(functions, 'updateOrgUserRole');
      await updateOrgUserRole({
        organizationId,
        userId,
        role: newRole,
      });
      await loadUsers();
    } catch (err: any) {
      console.error('Error updating user role:', err);
      alert(err.message || t('firm.updateRoleError'));
    }
  };

  const getRoleLabel = (role: UserRole) => {
    switch (role) {
      case 'org_admin': return t('firm.orgAdmin');
      case 'org_user': return t('firm.orgUser');
      case 'viewer': return t('firm.viewer');
      case 'admin': return t('admin.rolePlatformAdmin');
      case 'firm_lead': return t('firm.firmLead');
      case 'consultant': return t('firm.consultant');
      default: return role;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-wide" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('firm.usersFor', { name: organizationName })}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {/* Add User Form */}
          {showAddForm ? (
            <form className="inline-form" onSubmit={handleAddUser}>
              <input
                type="email"
                placeholder={t('firm.emailAddress')}
                value={newUserEmail}
                onChange={(e) => setNewUserEmail(e.target.value)}
                required
                autoFocus
              />
              <input
                type="text"
                placeholder={t('firm.nameOptional')}
                value={newUserName}
                onChange={(e) => setNewUserName(e.target.value)}
              />
              <select
                value={newUserRole}
                onChange={(e) => setNewUserRole(e.target.value as UserRole)}
              >
                {availableRoles.map((role) => (
                  <option key={role} value={role}>{getRoleLabel(role)}</option>
                ))}
              </select>
              <button type="submit" className="btn-primary" disabled={isAdding}>
                {isAdding ? t('general.loading') : t('general.add')}
              </button>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setShowAddForm(false)}
              >
                {t('general.cancel')}
              </button>
            </form>
          ) : (
            <button
              className="btn-add"
              onClick={() => setShowAddForm(true)}
              style={{ marginBottom: '16px' }}
            >
              {t('firm.addUser')}
            </button>
          )}

          {/* Users List */}
          {isLoading ? (
            <div className="loading">{t('general.loading')}</div>
          ) : users.length === 0 ? (
            <div className="empty-state">{t('firm.noUsers')}</div>
          ) : (
            <div className="org-users-list">
              {users.map((user) => (
                <div key={user.id} className="org-user-card">
                  <div className="org-user-info">
                    <h4>
                      {user.name}
                      {user.isPending && (
                        <span className="pending-badge"> ({t('firm.pending')})</span>
                      )}
                    </h4>
                    <span className="org-user-email">{user.email}</span>
                  </div>
                  <div className="org-user-actions">
                    <select
                      value={user.role}
                      onChange={(e) => handleUpdateRole(user.id, e.target.value)}
                      className="role-select"
                    >
                      {availableRoles.map((role) => (
                        <option key={role} value={role}>{getRoleLabel(role)}</option>
                      ))}
                    </select>
                    <button
                      className="btn-danger-small"
                      onClick={() => handleRemoveUser(user.id)}
                    >
                      {t('general.remove')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>
            {t('general.done')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrgManageUsers;
